import React, { useMemo } from 'react';
import { useLoginSummary } from '../../store/slices/dashboardSlice';
import ChartComponent from '../../components/charts/ChartComponent';
import { Card, CardBody, CardTitle } from 'reactstrap';
import { useTranslation } from 'react-i18next';

const AgentLoginSummary = () => {
  const { i18n, t } = useTranslation();
  const loginSummary = useLoginSummary();

  const timeToSeconds = (time) => {
    if (!time || time === '0' || time === '') return 0;
    const [hours, minutes, seconds] = time.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  };

  const secondsToTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  const pieChartData = useMemo(() => {
    if (!loginSummary) {
      console.error('Login summary data is not available.');
      return null;
    }

    const idleTime = timeToSeconds(loginSummary.idle_time);
    const talkTime = timeToSeconds(loginSummary.talk_time);
    const wrapUpTime = timeToSeconds(loginSummary.wrap_up_time);
    const holdTime = timeToSeconds(loginSummary.hold_time);
    const ringingTime = timeToSeconds(loginSummary.ringing_time);
    const dialingTime = timeToSeconds(loginSummary.dialing_time);
    const breakTime = timeToSeconds(loginSummary.break_time);

    const totalTime =
      idleTime + talkTime + wrapUpTime + holdTime + ringingTime + dialingTime + breakTime;

    if (totalTime === 0) {
      console.warn('Total time is zero, cannot create pie chart.');
      return null;
    }

    const data = [
      { name: t('idleTime'), value: (idleTime / totalTime) * 100, time: secondsToTime(idleTime) },
      { name: t('talkTime'), value: (talkTime / totalTime) * 100, time: secondsToTime(talkTime) },
      {
        name: t('wrapUpTime'),
        value: (wrapUpTime / totalTime) * 100,
        time: secondsToTime(wrapUpTime)
      },
      { name: t('holdTime'), value: (holdTime / totalTime) * 100, time: secondsToTime(holdTime) },
      {
        name: t('ringingTime'),
        value: (ringingTime / totalTime) * 100,
        time: secondsToTime(ringingTime)
      },
      {
        name: t('dialingTime'),
        value: (dialingTime / totalTime) * 100,
        time: secondsToTime(dialingTime)
      },
      { name: t('breakTime'), value: (breakTime / totalTime) * 100, time: secondsToTime(breakTime) }
    ];

    const chartSeries = data.map((item) => item.value);
    const chartLabels = data.map((item) => item.name);
    const chartTimes = data.map((item) => item.time);

    return {
      options: {
        labels: chartLabels,
        legend: {
          position: 'bottom'
        },
        tooltip: {
          y: {
            formatter: (value, { seriesIndex }) => {
              const time = chartTimes[seriesIndex];
              return time;
            }
          }
        },
        chart: {
          id: 'agent-login-summary',
          toolbar: {
            show: true,
            tools: {
              download: true
            }
          }
        },
        colors: [
          '#2cabe3', // Idle Time
          '#008000', // Talk Time
          '#775dd0', // Wrap Up Time
          '#ff4560', // Hold Time
          '#6376ff', // Ringing Time
          '#800080', // Dialing Time
          '#ffb22b' // Break Time
        ]
      },
      series: chartSeries
    };
  }, [loginSummary, i18n.language]);

  if (!pieChartData)
    return (
      <Card>
        <CardBody>
          <CardTitle tag="h5">{t('agentLoginSummary')}</CardTitle>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '300px' }}>
            {t('noDataAvailable')}
          </div>
        </CardBody>
      </Card>
    );

  return (
    <ChartComponent
      title={t('agentLoginTimeBreakdown')}
      chartType="pie"
      options={pieChartData.options}
      series={pieChartData.series}
      height={300}
    />
  );
};

export default AgentLoginSummary;
